import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Mic, Play, Pause, Heart, Share2, ShieldCheck, Clock, Globe, Music2 } from "lucide-react";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

export default function SongDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [song, setSong] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [saved, setSaved] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    api.get(`/songs/${id}`).then(({ data }) => setSong(data)).catch(() => { toast.error("Song not available."); nav("/songs"); });
  }, [id, nav]);

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (playing) { audioRef.current.pause(); } else { audioRef.current.play(); }
    setPlaying(!playing);
  };

  const save = async () => {
    if (!user) { nav("/login"); return; }
    const { data } = await api.post(`/songs/${id}/save`);
    setSaved(data.saved);
    toast.success(data.saved ? "Added to Saved Songs" : "Removed from Saved Songs");
  };

  const share = () => {
    const url = window.location.href;
    if (navigator.share) navigator.share({ title: song.title, url }).catch(() => {});
    else { navigator.clipboard.writeText(url); toast.success("Link copied!"); }
  };

  const sing = () => { user ? nav(`/studio/${id}`) : nav("/login", { state: { from: `/studio/${id}` } }); };

  if (!song) return <div className="min-h-[60vh]" />;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
      <div className="grid md:grid-cols-[300px_1fr] gap-8">
        <div>
          <div className="relative rounded-3xl overflow-hidden aspect-square glow-pink">
            <img src={song.cover_url} alt={song.title} className="w-full h-full object-cover" />
            <button onClick={togglePlay} data-testid="song-preview-btn" className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/50 transition-colors">
              <div className="w-16 h-16 rounded-full bg-neon-pink flex items-center justify-center glow-pink">
                {playing ? <Pause className="w-7 h-7 text-white" /> : <Play className="w-7 h-7 text-white ml-1" />}
              </div>
            </button>
          </div>
          <audio ref={audioRef} src={`${API}/songs/${id}/track`} onEnded={() => setPlaying(false)} />
        </div>

        <div>
          <Badge className="bg-neon-cyan/20 text-neon-cyan border-0 mb-3">{song.genre}</Badge>
          <h1 className="font-head text-4xl sm:text-5xl font-black tracking-tighter">{song.title}</h1>
          <p className="text-xl text-muted-foreground mt-2">{song.artist}</p>

          <div className="flex flex-wrap gap-4 mt-5 text-sm text-muted-foreground">
            <span className="flex items-center gap-1.5"><Globe className="w-4 h-4" /> {song.language} · {song.country}</span>
            <span className="flex items-center gap-1.5"><Clock className="w-4 h-4" /> {Math.floor(song.duration / 60)}:{String(song.duration % 60).padStart(2, "0")}</span>
            <span className="flex items-center gap-1.5"><Music2 className="w-4 h-4" /> {song.difficulty}</span>
          </div>

          <div className="flex flex-wrap gap-3 mt-7">
            <Button onClick={sing} data-testid="sing-now-btn" className="rounded-full bg-neon-pink hover:bg-neon-pink/90 glow-pink font-bold px-8 py-6"><Mic className="w-5 h-5 mr-2" /> Sing Now</Button>
            <Button onClick={save} variant="outline" data-testid="save-song-btn" className="rounded-full glass border-white/20 hover:bg-white/10 py-6"><Heart className={`w-5 h-5 mr-2 ${saved ? "fill-neon-pink text-neon-pink" : ""}`} /> Save</Button>
            <Button onClick={share} variant="outline" data-testid="share-song-btn" className="rounded-full glass border-white/20 hover:bg-white/10 py-6"><Share2 className="w-5 h-5 mr-2" /> Share</Button>
          </div>

          <div className="mt-8 card-surface rounded-2xl p-5">
            <p className="flex items-center gap-2 text-sm font-semibold text-green-400 mb-3"><ShieldCheck className="w-4 h-4" /> Licensed & available for recording</p>
            <div className="grid grid-cols-2 gap-y-2 text-sm">
              <span className="text-muted-foreground">Rights holder</span><span className="text-right truncate">{song.rights_holder?.split("—")[0] || "SINGVI"}</span>
              <span className="text-muted-foreground">Provider</span><span className="text-right">{song.provider}</span>
              <span className="text-muted-foreground">Territory</span><span className="text-right capitalize">{song.territory}</span>
              <span className="text-muted-foreground">Recording</span><span className="text-right text-green-400">{song.recording_allowed ? "Allowed" : "Restricted"}</span>
              <span className="text-muted-foreground">Download</span><span className="text-right">{song.download_allowed ? "Allowed" : "Restricted"}</span>
            </div>
          </div>
        </div>
      </div>

      {song.lyrics?.length > 0 && (
        <div className="mt-10 card-surface rounded-2xl p-6">
          <h3 className="font-head text-xl font-bold mb-4">Lyrics</h3>
          <div className="space-y-1.5 text-muted-foreground">
            {song.lyrics.map((l, i) => <p key={i}>{l.line}</p>)}
          </div>
        </div>
      )}
    </div>
  );
}
